import { Injectable, inject } from '@angular/core';
import { Observable, map, of } from 'rxjs';

import { GuaranteeApplicationsService } from './guarantee-applications.service';
import { PharmacologicalItemMetaData, Prescription, PrescriptionTypes } from '../models/prescription.interface';

export const SIMILAR_PRESCRIPTIONS_LIMIT = 10;
export const SIMILAR_PRESCRIPTIONS_RECENT_DAYS = 30;

const SERVICE_PRESCRIPTION_TYPES = new Set<PrescriptionTypes>([
  PrescriptionTypes.Appointment,
  PrescriptionTypes.InstrumentalDiagnostic,
  PrescriptionTypes.LaboratoryService,
]);

@Injectable({
  providedIn: 'root',
})
export class SimilarPrescriptionsService {
  private readonly guaranteeApplicationsService = inject(GuaranteeApplicationsService);

  getSimilarPrescriptions(prescription: Prescription, fallbackUserId?: string): Observable<Prescription[]> {
    const term = this.resolveIdentifier(prescription);
    if (!term) return of([]);

    const normalizedTerm = this.normalize(term);
    const recentThreshold = new Date();
    recentThreshold.setDate(recentThreshold.getDate() - SIMILAR_PRESCRIPTIONS_RECENT_DAYS);

    return this.guaranteeApplicationsService
      .searchGuaranteeApplications({
        userId: prescription.userId ?? fallbackUserId,
        prescriptionName: term,
        page: 1,
        pageSize: SIMILAR_PRESCRIPTIONS_LIMIT,
      })
      .pipe(
        map(response =>
          (response.value ?? [])
            .flatMap(guarantee => guarantee.prescriptions ?? [])
            .filter(
              item =>
                item.id !== prescription.id &&
                this.normalize(this.resolveIdentifier(item)) === normalizedTerm &&
                new Date(item.startDate) >= recentThreshold
            )
            .slice(0, SIMILAR_PRESCRIPTIONS_LIMIT)
        )
      );
  }

  private resolveIdentifier(prescription: Prescription): string {
    if (prescription.type === PrescriptionTypes.Drugs) {
      return (prescription.metadata as PharmacologicalItemMetaData)?.inn || prescription.name;
    }
    if (SERVICE_PRESCRIPTION_TYPES.has(prescription.type)) {
      return prescription.metadata?.['code'] || prescription.name;
    }
    return prescription.name;
  }

  private normalize(value: string): string {
    return (value ?? '').trim().toLowerCase();
  }
}
